<template>
    <div>
        <custom-table
            :title="$t('pages.ecommerce.product.resource.title')"
            rowKey="id"
            :items="table.data || []"
            :loading="table.loading"
            selectableRows
            :emptyTableText="$t('pages.ecommerce.product.resource.no_set_resource')"
            :columns="fields"
            :actions="tableActions"
            @action="handleClickAction"
            @selectableRows="handleSelectedRow">
            <template v-slot:resource_code="{ row: record }">
                {{ resourceLabel(record.resource_code) }}
            </template>
            <template v-slot:amount="{ row: record }">
                {{ record.amount }} <span class="text-muted">{{ resourceUnit(record.resource_code) }}</span>
            </template>
            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <el-tooltip :content="$t('common.edit')" placement="top">
                        <a @click="editResource(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                            </span>
                        </a>
                    </el-tooltip>
                    <a @click="deleteResource(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </div>
            </template>
        </custom-table>

        <div class="modal fade" id="kt_modal_product_resource" ref="resourceModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered mw-650px">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="fw-bolder">{{ form.title }}</h2>
                        <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                            <span class="svg-icon svg-icon-1">
                                <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                            </span>
                        </div>
                    </div>
                    <el-form @submit.prevent="onSubmit()" :model="form.data" ref="resourceForm">
                        <div class="modal-body py-10 px-lg-17">
                            <div class="fv-row mb-1 mt-3">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.resource.cols.resource_code') }}</label>
                                <el-form-item prop="resource_code" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.resource_code" :disabled="form.updateStatus" class="w-100" :placeholder="$t('common.chooseSelect')">
                                        <el-option
                                            v-for="code in availableCodes"
                                            :key="code"
                                            :value="code"
                                            :label="resourceLabel(code)"/>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-1 mt-3">
                                <label class="required fs-6 fw-bold mb-2">
                                    {{ $t('pages.ecommerce.product.resource.cols.amount') }}
                                    <span v-if="form.data.resource_code" class="text-muted">({{ resourceUnit(form.data.resource_code) }})</span>
                                </label>
                                <el-form-item prop="amount" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.data.amount" oninput="value=value.replace(/[^0-9.]/g,'')"/>
                                </el-form-item>
                                <small v-if="form.data.resource_code" class="text-muted d-block mt-1">{{ resourceHelp(form.data.resource_code) }}</small>
                            </div>
                        </div>

                        <div class="modal-footer flex-center">
                            <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                                <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                <span v-if="form.loading" class="indicator-progress">
                                    {{ $t("messages.wait") }}
                                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

const RESOURCE_CODES = ['STORAGE_MB', 'STOCK_ITEM_LIMIT', 'CUSTOM_DOMAIN'];

export default {
    name: "ProductResourceList",
    props: ['productID'],
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.product.resource.cols.resource_code"), scopedSlots: {customRender: "resource_code"}},
                {name: this.$t("pages.ecommerce.product.resource.cols.amount"), scopedSlots: {customRender: "amount"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            tableActions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"}
            ],
            selectedRowKeys: [],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {},
            },
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.product.resource.table;
        },
        availableCodes() {
            const used = (this.table.data || []).map(r => r.resource_code);
            if (this.form.updateStatus) {
                return RESOURCE_CODES.filter(c => c === this.form.data.resource_code || !used.includes(c));
            }
            return RESOURCE_CODES.filter(c => !used.includes(c));
        },
    },
    mounted() {
        this.refreshList();
    },
    methods: {
        refreshList() {
            this.$store.dispatch('ecommerce/product/resource/get', {product_id: this.productID});
        },
        resourceLabel(code) {
            return this.$t('pages.ecommerce.product.resource.codes.' + code);
        },
        resourceUnit(code) {
            return this.$t('pages.ecommerce.product.resource.units.' + code);
        },
        resourceHelp(code) {
            return this.$t('pages.ecommerce.product.resource.help.' + code);
        },
        addResource() {
            this.resetForm();
            this.form.title = this.$t('pages.ecommerce.product.resource.newTitle');
            this.showModal(this.$refs.resourceModal);
        },
        editResource(record) {
            this.resetForm();
            this.form.updateStatus = true;
            this.form.title = this.$t('pages.ecommerce.product.resource.editTitle');
            this.form.data = {
                id: record.id,
                resource_code: record.resource_code,
                amount: record.amount,
            };
            this.showModal(this.$refs.resourceModal);
        },
        deleteResource(record) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/product/resource/delete', {
                        id: record.id,
                        product_id: this.productID,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => this.refreshList());
                    }
                },
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "new":
                    this.addResource();
                    break;
                case "refresh":
                    this.refreshList();
                    break;
                case "delete":
                    this.bulkDelete();
                    break;
            }
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        async bulkDelete() {
            const ids = this.selectedRowKeys.flat();
            if (!ids.length) {
                this.$message.warning(this.$t('messages.selectRow'));
                return;
            }
            for (const id of ids) {
                await this.$store.dispatch('ecommerce/product/resource/delete', {
                    id,
                    product_id: this.productID,
                });
            }
            this.selectedRowKeys = [];
            this.refreshList();
        },
        onSubmit() {
            this.$refs.resourceForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                try {
                    const response = this.form.updateStatus
                        ? await this.$store.dispatch('ecommerce/product/resource/update', {
                            id: this.form.data.id,
                            product_id: this.productID,
                            formData: {amount: this.form.data.amount},
                        })
                        : await this.$store.dispatch('ecommerce/product/resource/add', {
                            product_id: this.productID,
                            formData: {
                                resource_code: this.form.data.resource_code,
                                amount: this.form.data.amount,
                            },
                        });

                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.resourceModal);
                            this.refreshList();
                        });
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    resource_code: undefined,
                    amount: null,
                },
            };
        },
    }
}
</script>
